import { CustomButton } from "@/components/ui/Buttons";

import AddActorIcon from "@/assets/icons/AddActorIcon.svg";
import ArrowDown from "@/assets/icons/ArrowDownOrange.svg";
import TopPerformer from "@/assets/icons/TopPerformer.svg";
import ExperienceActor from "@/assets/icons/ExperienceActor.svg";
import DeliveryTime from "@/assets/icons/DeliveryTime.svg";
import ImageAlt from "@/assets/icons/ImageAlt.svg";
import { TCreator } from "@/types/projectType";

type ActorDetailsProps = {
  isAssignedActorExits?: boolean;
  assigned?: boolean;
  handleUnAssignActor?: () => Promise<void>;
  handleAssignActor?: (id: string) => Promise<void>;
  showAssignButton?: boolean;
  actor: TCreator;
};

const ActorDetails = ({
  isAssignedActorExits,
  assigned,
  handleUnAssignActor,
  handleAssignActor,
  actor,
  showAssignButton = false,
}: ActorDetailsProps) => {
  if (!actor) return null;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center pb-5 border-b border-[#E5E9EB] gap-y-5">
        <p className="text-xl leading-7 tracking-[-1%] font-semibold">
          Actor Details
        </p>

        {showAssignButton && (
          <>
            {assigned ? (
              <>
                {" "}
                <CustomButton
                  onClick={handleUnAssignActor}
                  color="#FF6600"
                  variant="outline"
                  padding="8px 12px"
                  className="flex items-center gap-2 leading-5 tracking-[-1%]"
                  fontSize="12px"
                  fontWeight="500"
                  border="1px solid #FF6600"
                >
                  <span>
                    <AddActorIcon />
                  </span>
                  <span>UnAssign Project</span>
                  <span>
                    <ArrowDown />
                  </span>
                </CustomButton>
              </>
            ) : (
              <>
                {!isAssignedActorExits && (
                  <CustomButton
                    onClick={() => handleAssignActor?.(actor?._id)}
                    color="#FF6600"
                    variant="outline"
                    padding="8px 12px"
                    className="flex items-center gap-2 leading-5 tracking-[-1%]"
                    fontSize="12px"
                    fontWeight="500"
                    border="1px solid #FF6600"
                  >
                    <span>
                      <AddActorIcon />
                    </span>
                    <span>Assign Project</span>
                    <span>
                      <ArrowDown />
                    </span>
                  </CustomButton>
                )}
              </>
            )}
          </>
        )}
      </div>
      {/* Actor name */}
      <div className="py-6 border-b border-[#E5E9EB] space-y-6">
        <div className="flex flex-col sm:flex-row gap-2 items-center">
          <div className="flex items-center gap-1">
            <img
              src={actor?.profilePicture}
              className="h-8 w-8 rounded-full"
              alt="Actor image"
            />
            <p className="text-lg leading-5 tracking-[-1.5%] font-medium">
              {actor?.first_name} {actor?.last_name}
            </p>
          </div>
          <div className="bg-[#667085] rounded-full size-1"></div>
          <p className="text-[#667085] text-sm leading-5 tracking-[-1.5%]">
            {actor?.age} <span>({actor?.gender.slice(0, 1)})</span>
          </p>
          <div className="bg-[#667085] rounded-full size-1"></div>
          <p className="text-[#6941C6] text-xs leading-[18px] font-medium bg-[#F9F5FF] px-2 py-[2px] rounded-2xl">
            {actor?.actorTag}
          </p>
        </div>
        {/* Performance badge */}
        <div className="flex flex-wrap items-center gap-3">
          <Badge icon={<TopPerformer />} label="Top performer" />
          <Badge icon={<ExperienceActor />} label="Experience Actor" />
          <Badge icon={<DeliveryTime />} label="Submitted record" />
        </div>
      </div>
      {/* Actor details */}
      <div className="py-6 flex flex-wrap gap-y-2 items-center justify-between">
        <InfoItem label="Country" value={actor?.address?.country} />
        <InfoItem label="Phone number" value={actor?.phoneNumber} />
        <InfoItem label="Email" value={actor?.email as string} />
        <InfoItem label="Language" value={actor?.language} />
      </div>
      {/* <div>
        <h5 className="text-[#475467] text-[13px] leading-4 tracking-[-0,75%] font-medium">
          Wardrobe styles
        </h5>
        <div className="flex gap-3 items-center flex-wrap mt-3">
          {actor.wardrobeStyles?.map((item, i) => (
            <WardrobeItem key={i} label={item} />
          ))}
        </div>
      </div> */}
      {/* Actor device */}
      <div className="flex flex-wrap items-center gap-6">
        <ActorDevice
          label="Camera/Smartphone with Tripod"
          value={actor?.hasTripod ? "Yes" : "No"}
        />
        <ActorDevice
          label="Lighting"
          value={actor?.hasArtificialLight ? "Yes" : "No"}
        />
        <ActorDevice
          label="Green Screen"
          value={actor?.greenScreen ? "Yes" : "No"}
        />
        <ActorDevice
          label="Peers/Relatives Joining them?"
          value={actor?.hasRelativesInVideo ? "Yes" : "No"}
        />
      </div>
      {/* Different background */}
      <div className="space-y-3 pt-6">
        <h5 className="text-[#475467] text-[13px] leading-4 tracking-[-0,75%] font-medium">
          Different background
        </h5>
        <div className="flex flex-wrap gap-3">
          {actor.availableBackgrounds?.map((item, i) => (
            <WardrobeItem label={item.name} key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActorDetails;

// Badge

interface BadgeProps {
  icon: JSX.Element;
  label: string;
}

const Badge = ({ icon, label }: BadgeProps) => (
  <div className="flex gap-1 items-center border border-[#17457D17] bg-[#17457D05] rounded-lg py-1.5 px-2.5">
    <span>{icon}</span>
    <p className="text-[#667085] text-xs leading-[18px] tracking-[-1%] font-medium">
      {label}
    </p>
  </div>
);

// Info item
interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem = ({ label, value }: InfoItemProps) => (
  <div className="space-y-1">
    <p className="text-[#98A2B3] text-[10px] leading-4 tracking-[-1%] font-medium">
      {label}
    </p>
    <p className="text-[#667085] text-xs leading-[18px] tracking-[-1%] font-medium uppercase">
      {value}
    </p>
  </div>
);

// Wardrobe style
const WardrobeItem = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 border border-[#F1F1F1] rounded-[10px] bg-[#F7F7F7] p-3">
    <span>
      <ImageAlt />
    </span>
    <p className="text-[#475467] text-[13px] leading-4 tracking-[-0.75%] font-medium">
      {label}
    </p>
  </div>
);

// Actor Device
interface ActorDeviceProps {
  label: string;
  value: string;
}

const ActorDevice = ({ label, value }: ActorDeviceProps) => {
  return (
    <div className="pt-6">
      <h5 className="text-[#475467] text-[13px] leading-4 tracking-[-0,75%] font-medium mb-3">
        {label}
      </h5>
      <p className="text-[#667085] text-[13px] leading-4 tracking-[-0.75%] font-medium inline-block border border-[#F1F1F1] rounded-[10px] bg-[#F7F7F7] p-3">
        {value}
      </p>
    </div>
  );
};
